import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import URL from '../../../constants/url';
import Header from '../../../components/comm/Head';
import Navbar from '../../../components/comm/Navbar';
import HeroCard from '../../../components/home/HeroCard';
import HealthReport from '../../../components/home/HealthReport';
import RecommendContent from '../../../components/home/RecommendContent';
import AiRecommendBox from '../../../components/home/AiRecommendBox';
import style from './HomeMain.module.css';

import heroDiet from '../../../images/common/img_main_mascot_01.svg';
import heroExercise from '../../../images/common/img_main_mascot_02.svg';
import camera from '../../../images/common/i_camera.svg';
import stepsW from '../../../images/common/i_steps_white.svg';
import axiosApi from '../../../api/daonAxios';
import { savePushToken } from '../../../api/pushAPI';
import MessageTypes from '../../../bridge/messageTypes';
import { STEPS_STATUS, useHealthSteps } from '../../../hook/useHealthSteps';
import { getBloodPressureResultCode } from '../../../utils/bp';
import { getPatntHealthStatus } from '../../../utils/patnt'

import dayjs from 'dayjs';

// /** 걸음수 달성 기준. 이 값을 넘으면 '양호'로 표시한다. */
// const STEPS_GOAL = 10000;

// import heroDietVideo from '../../../images/common/character_diet.mp4';
// import heroExerciseVideo from '../../../images/common/character_exercise.mp4';

const WEEKLY_EXERCISE_MIN_GOAL = 30;

const MAIN_TABS = [
    { key: 'diet', label: '식단 관리' },
    { key: 'exercise', label: '운동 관리' },
];

const SUB_TABS = [
    { key: 'daily', label: '일일 리포트' },
    { key: 'goal', label: '신장 건강목표' },
];

/* 바깥에서 `/home-main?tab=exercise` 로 탭을 지정할 수 있게 한다 */
const pickTab = (tabs, value, fallback) =>
    tabs.some((t) => t.key === value) ? value : fallback;

/* ── 상단 히어로 영역 (메인탭 기준) ── */
const HERO_DATA = {
    diet: {
        label: '오늘의 나트륨 섭취량',
        value: '1,250', unit: 'mg', goal: '/ 2,000mg',
        level: 'good', levelText: '양호',
        actionText: '식단 촬영',
        image: heroDiet,
        //video: heroDietVideo,   // image → video
        btnImage: camera,
        onAction: URL.FOOD_RECORD_MAIN
    },
    exercise: {
        label: '오늘의 운동 시간',
        value: '30', unit: '분', goal: '/ 300kcal 소모',
        level: 'excellent', levelText: '우수',
        actionText: '운동 기록',
        image: heroExercise ,
        //video: heroExerciseVideo,
        btnImage: stepsW,
        onAction: URL.EXERCISE_RECORD_MAIN
    },
};


// 일일 리포트 상단 주간 목표 카드
// const WEEKLY_GOALS = [
//     {
//         title: '나트륨 섭취',
//         condition: '2,000mg 이하 / 주 5회 이상',
//         days: ['success', 'none', 'fail', 'success', 'none', 'none', 'none'], // 일~토
//         color: 'blue',
//     },
// ];

// const CONTENT_DATA = [
//     { id: 1, thumb: require('../../../images/common/img_commed_sample_01.png'), title: '당뇨병 관리, 무엇을 해야 할까요?', tag: '# 고혈압의 식사원칙' },
//     { id: 2, thumb: require('../../../images/common/img_commed_sample_02.png'), title: '당뇨병일 땐, 어떻게 먹어야 할까요?', tag: '# 고혈압의 식사원칙' },
// ];

const EXERCISE_CALORIE_GOAL = 1000;

/* ── 컴포넌트 ── */
const HomeMain = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [mainTab, setMainTab] = useState(
        () => pickTab(MAIN_TABS, searchParams.get('tab'), 'diet'),
    );
    const [subTab, setSubTab] = useState(
        () => pickTab(SUB_TABS, searchParams.get('sub'), 'daily'),
    );

    // 탭 인디케이터
    const tabBarRef = useRef(null);
    const [indicatorStyle, setIndicatorStyle] = useState({});

    // 서브 탭 인디케이터
    const subTabBarRef = useRef(null);
    const [subIndicatorStyle, setSubIndicatorStyle] = useState({});

    const [homeData, setHomeData] = useState({
        dietDayInfo: {},
        usrCalorList: [], // 추가
        currentBmi: {},
        weeklySodium: [],
        weeklyExercise: [],
        currentBrssr: {},
        nutritionGoal: {},
        exerToday: {},
    });

    const [goalData, setGoalData] = useState({});
    
    const [latestCheckup, setLatestCheckup] = useState({});

    const [contents, setContents] = useState([]);

    //식단 ai용
    const aiRequestRef = useRef(false);
    const aiPollingIdRef = useRef(null);
    const aiPollingTimerRef = useRef(null);
    
    const [aiComment, setAiComment] = useState('');
    const [aiCommentLoading, setAiCommentLoading] = useState(false);
    //식단 ai용

    //운동 ai용
    const exerciseAiRequestRef = useRef(false);
    const exerciseAiPollingIdRef = useRef(null);
    const exerciseAiPollingTimerRef = useRef(null);

    const [exerciseAiComment, setExerciseAiComment] = useState('');
    const [exerciseAiCommentLoading, setExerciseAiCommentLoading] = useState(false);
    //운동 ai용

    const updateIndicator = (key) => {
        const bar = tabBarRef.current;
        if (!bar) return;
        const btn = bar.querySelector(`[data-tab="${key}"]`);
        if (!btn) return;
        setIndicatorStyle({ left: btn.offsetLeft, width: btn.offsetWidth });
    };

    const updateSubIndicator = (key) => {
        const bar = subTabBarRef.current;
        if (!bar) return;
        const btn = bar.querySelector(`[data-subtab="${key}"]`);
        if (!btn) return;
        setSubIndicatorStyle({
            left: btn.offsetLeft,
            width: btn.offsetWidth,
        });
    };

    useEffect(() => {
        return () => {
            clearTimeout(aiPollingTimerRef.current);

            aiPollingTimerRef.current = null;
            aiPollingIdRef.current = null;
            aiRequestRef.current = false;

            exerciseAiPollingTimerRef.current = null;
            exerciseAiPollingIdRef.current = null;
            exerciseAiRequestRef.current = false;
        };
    }, []);

    // 홈에 있는 채로 탭 지정이 또 들어오면 마운트가 안 되므로 여기서 맞춘다
    useEffect(() => {
        setMainTab((cur) => pickTab(MAIN_TABS, searchParams.get('tab'), cur));
        setSubTab((cur) => pickTab(SUB_TABS, searchParams.get('sub'), cur));
    }, [searchParams]);

    useEffect(() => { updateIndicator(mainTab); }, [mainTab]);
    useEffect(() => { updateSubIndicator(subTab); }, [subTab]);

    useEffect(() => {
        const onResize = () => {
            updateIndicator(mainTab);
            updateSubIndicator(subTab);
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [mainTab, subTab]);

    const formatNumber = (value, maxFractionDigits = 0) => {
        if (value == null) {
            return '-';
        }

        return Number(value).toLocaleString('ko-KR', {
            maximumFractionDigits: maxFractionDigits,
        });
    };

    const hero = useMemo(() => {

        /* ── 식단 관리 ── */
        if (mainTab === 'diet') {

            const sodium = homeData.dietDayInfo?.sodium;
            const sodiumGoal = homeData.nutritionGoal?.sodiumGoal;

            let level = 'none';
            let levelText = '';

            if (sodium != null) {
                const sodiumValue = Number(sodium);

                
                if (sodiumValue <= 1000) {
                    level = 'excellent';
                    levelText = '우수';
                } else if (sodiumValue <= 2000) {
                    level = 'good';
                    levelText = '양호';
                } else {
                    level = 'warn';
                    levelText = '주의';
                }
            }

            return {
                ...HERO_DATA.diet,

                value: sodium != null
                    // ? formatNumber(sodium)
                    ? Math.round(Number(sodium)).toLocaleString('ko-KR')
                    : '0',

                unit: 'mg',

                goal: sodiumGoal != null
                    ? `/ ${formatNumber(sodiumGoal)}mg`
                    : '',

                level,
                levelText,
            };
        }

        /* ── 운동 관리 ── */
        const exerciseMin = homeData.exerToday?.totMin;
        const exerciseCalorie = homeData.exerToday?.totCalor;

        let level = 'none';
        let levelText = '';

        // if (exerciseCalorie != null) {
        //     if (Number(exerciseCalorie) >= EXERCISE_CALORIE_GOAL) {
        //         level = 'excellent';
        //         levelText = '우수';
        //     } else {
        //         level = 'short';
        //         levelText = '부족';
        //     }
        // }

        if (exerciseMin != null) {
            if (Number(exerciseMin) >= 30) {
                level = 'excellent';
                levelText = '우수';
            } else if (Number(exerciseMin) >= 15) {
                level = 'excellent';
                levelText = '양호';
            } else {
                level = '';
                levelText = '';
                // level = 'short';
                // levelText = '부족';
            }
        }

        return {
            ...HERO_DATA.exercise,

            value: exerciseMin != null
                ? formatNumber(exerciseMin)
                : '-',

            unit: '분',

            goal: exerciseCalorie != null
                ? `/ ${formatNumber(exerciseCalorie)}kcal 소모`
                : '',

            level,
            levelText,
        };

    }, [mainTab, homeData]);

    const handleAiChat = useCallback(() => {
        navigate(URL.CHATBOT , { state: { mode: mainTab, aiComment: mainTab  === 'diet' ?  aiComment : exerciseAiComment} });
    },[mainTab, exerciseAiComment, aiComment]);

    ///////////////////////

    useEffect(() => {
        getHomeInitdata();
        getRandomHealthContent();
    }, []);


    // ========================================================================
    // 푸시 토큰 갱신 (홈 진입 시)
    // ========================================================================

    // 전송 중인 푸시 토큰 (결과 메시지가 여러 번 와도 요청은 하나만)
    const pushTokenSendingRef = useRef(null);

    // 앱에서 받은 FCM 토큰을 서버(+dpmessage)에 등록
    const updatePushToken = async (pushTknVal) => {
        if (sessionStorage.getItem('pushTokenSynced') === pushTknVal) return;
        if (pushTokenSendingRef.current === pushTknVal) return;
        pushTokenSendingRef.current = pushTknVal;

        try {
            if (await savePushToken(pushTknVal)) {
                sessionStorage.setItem('pushTokenSynced', pushTknVal);
                console.log('[push] 토큰 서버 등록 완료');
            } else {
                console.warn('[push] 토큰 서버 등록 실패');
            }
        } catch (err) {
            console.error('[push] 토큰 서버 등록 오류', err);
        } finally {
            pushTokenSendingRef.current = null;
        }
    };

    // 홈 진입 시 푸시 토큰 갱신
    useEffect(() => {
        if (!window.ReactNativeWebView) return;

        const handleNativeMessage = (event) => {
            let data = event.data;
            if (typeof data === 'string') {
                try {
                    data = JSON.parse(data);
                } catch (e) {
                    return;
                }
            }
            if (data?.type !== MessageTypes.NOTIFICATION_PERMISSION_RESULT) return;
            if (!data.granted || !data.token) return;

            sessionStorage.setItem('pushToken', data.token);
            updatePushToken(data.token);
        };

        window.addEventListener('message', handleNativeMessage);
        window.ReactNativeWebView.postMessage(
            JSON.stringify({ type: MessageTypes.CHECK_NOTIFICATION_PERMISSION })
        );

        return () => window.removeEventListener('message', handleNativeMessage);
    }, []);

    /* 알림으로 앱이 켜진 경우 앱이 경로를 물고 있다. 홈까지 온 지금이 받아갈 시점이다. */
    useEffect(() => {
        if (!window.ReactNativeWebView) return;
        window.ReactNativeWebView.postMessage(
            JSON.stringify({ type: MessageTypes.PUSH_NAVIGATE_PENDING })
        );
    }, []);


    /**
     * 걸음수 홈 진입 시 조회하면서 서버 동기화도 같이 돈다
     * (최초 30일 소급 → 이후 최근 7일 갱신, 훅이 알아서 판단).
     */
    const { status: stepsStatus, daily: stepsDaily, reload: reloadSteps } =
        useHealthSteps({ days: 1 });

    const todaySteps =
        stepsStatus === STEPS_STATUS.READY
            ? stepsDaily[stepsDaily.length - 1]?.steps ?? 0
            : null;

    /** 걸음수 행만 실데이터로 교체한다. 나머지 지표는 아직 목업. */
    const getBmiStatus = (rsltCd) => {
        switch (rsltCd) {
            case 'N':
                return {
                    level: 'good',
                    levelText: '정상',
                };

            case 'C':
                return {
                    level: 'warn',
                    levelText: '주의',
                };

            case 'D':
                return {
                    level: 'danger',
                    levelText: '위험',
                };

            case 'L':
                return {
                    level: 'underweight',
                    levelText: '저체중',
                };

            default:
                return {
                    level: 'none',
                    levelText: '',
                };
        }
    };

    const getBloodPressureStatus = (rsltCd) => {
        switch (rsltCd) {
            case 'N':
                return {
                    level: 'good',
                    levelText: '정상',
                };

            case 'C':
                return {
                    level: 'warn',
                    levelText: '주의',
                };

            case 'D':
                return {
                    level: 'danger',
                    levelText: '위험',
                };

            default:
                return {
                    level: 'none',
                    levelText: '',
                };
        }
    };

    const dailyList = useMemo(() => {

        const {
            dietDayInfo,
            currentBmi,
            currentBrssr,
            nutritionGoal,
            exerToday,
        } = homeData;

        /* ── 섭취 칼로리 ── */
        const energy = dietDayInfo?.energy;
        const energyGoal = nutritionGoal?.energyGoal;

        let calorieLevel = 'none';
        let calorieLevelText = '';

        if (energy != null && energyGoal != null) {
            if (Number(energy) < Number(energyGoal)) {
                calorieLevel = 'short';
                calorieLevelText = '부족';
            } else if (Number(energy) > Number(energyGoal)) {
                calorieLevel = 'warn';
                calorieLevelText = '초과';
            } else {
                calorieLevel = 'good';
                calorieLevelText = '양호';
            }
        }

        /* ── 걸음수: Health Connect 기준 ── */
        const stepsLevel = '';
        const stepsLevelText = '';
        // const stepsLevel =
        //     todaySteps == null
        //         ? 'none'
        //         : Number(todaySteps) >= STEPS_GOAL
        //             ? 'good'
        //             : 'short';
        //
        // const stepsLevelText =
        //     todaySteps == null
        //         ? ''
        //         : Number(todaySteps) >= STEPS_GOAL
        //             ? '양호'
        //             : '부족';

        /* ── 운동 ── */
        const exerciseCalorie = exerToday?.totCalor;
        const exerciseMin = exerToday?.totMin;

        let exerciseLevel = '';

        let exerciseLevelText = '';

        if (exerciseMin != null) {
            if (Number(exerciseMin) >= 30) {
                exerciseLevel = 'excellent';
                exerciseLevelText = '우수';
            } else if (Number(exerciseMin) >= 15) {
                exerciseLevel = 'good';
                exerciseLevelText = '양호';
            } else {
                exerciseLevel = '';
                exerciseLevelText = '';
                // exerciseLevel = 'short';
                // exerciseLevelText = '부족';
            }
        }

        /* ── 혈압 / BMI ── */
        const bloodPressureStatus = getBloodPressureStatus(currentBrssr?.rsltCd);

        const bmiStatus = getBmiStatus(currentBmi?.rsltCd);

        return [
            {
                icon: 'calorie',
                label: '섭취 칼로리',
                level: calorieLevel,
                levelText: calorieLevelText,
                // value: formatNumber(energy),
                value: dietDayInfo?.energy != null
                       ? `${Math.round(Number(energy)).toLocaleString('ko-KR')}`
                       : '-',
                goal: energyGoal != null
                    ? `/${formatNumber(energyGoal)}kcal`
                    : '',
                unit: '',
                nutrients: [
                    {
                        label: '탄수화물',
                        value: dietDayInfo?.carbohydrate != null
                            // ? `${formatNumber(dietDayInfo.carbohydrate, 1)}g`
                            ? `${(Math.round(Number(dietDayInfo.carbohydrate) * 10) / 10).toLocaleString('ko-KR')}g`
                            : '-',
                        goal: nutritionGoal?.carbohydrateGoal != null
                            ? `/${formatNumber(nutritionGoal.carbohydrateGoal)}g`
                            : '',
                    },
                    {
                        label: '지방',
                        value: dietDayInfo?.fat != null
                            // ? `${formatNumber(dietDayInfo.fat, 1)}g`
                            ? `${(Math.round(Number(dietDayInfo.fat) * 10) / 10).toLocaleString('ko-KR')}g`
                            : '-',
                        goal: nutritionGoal?.fatGoal != null
                            ? `/${formatNumber(nutritionGoal.fatGoal)}g`
                            : '',
                    },
                    {
                        label: '단백질',
                        value: dietDayInfo?.protein != null
                            // ? `${formatNumber(dietDayInfo.protein, 1)}g`
                            ? `${(Math.round(Number(dietDayInfo.protein) * 10) / 10).toLocaleString('ko-KR')}g`
                            : '-',
                        goal: nutritionGoal?.proteinGoal != null
                            ? `/${formatNumber(nutritionGoal.proteinGoal)}g`
                            : '',
                    },
                    {
                        label: '나트륨',
                        value: dietDayInfo?.sodium != null
                            // ? `${formatNumber(dietDayInfo.sodium)}mg`
                            ? `${Math.round(Number(dietDayInfo.sodium)).toLocaleString('ko-KR')}mg`
                            : '-',
                        goal: nutritionGoal?.sodiumGoal != null
                            ? `/${formatNumber(nutritionGoal.sodiumGoal)}mg`
                            : '',
                    },
                ],
            },

            {
                icon: 'steps',
                label: '걸음수',
                level: stepsLevel,
                levelText: stepsLevelText,
                value: todaySteps == null
                    ? '-'
                    : Number(todaySteps).toLocaleString(),
                goal: '걸음',
                unit: '',
                btnType: 'sync',
            },

            {
                icon: 'exercise',
                label: '운동',
                level: exerciseLevel,
                levelText: exerciseLevelText,
                value: formatNumber(exerciseCalorie),
                goal: 'kcal',
                extra: exerciseMin != null
                    ? formatNumber(exerciseMin)
                    : '-',
                unit: '분',
            },

            {
                icon: 'bloodpress',
                label: '혈압',
                level: bloodPressureStatus.level,
                levelText: bloodPressureStatus.levelText,
                value:
                    currentBrssr?.brssrTop != null &&
                    currentBrssr?.brssrLwet != null
                        ? `${currentBrssr.brssrTop}/${currentBrssr.brssrLwet}`
                        : '-',
                goal: '',
                unit: 'mmHg',
            },

            {
                icon: 'bmi',
                label: '체질량',
                level: bmiStatus.level,
                levelText: bmiStatus.levelText,
                value: currentBmi?.bmi != null
                    ? formatNumber(currentBmi.bmi, 2)
                    : '-',
                goal: '',
                unit: '',
            },
        ];

    }, [homeData, todaySteps]);

    const getHomeInitdata = useCallback(async () => {
        const jToken = localStorage.getItem("jToken");

        const requestOption = {
            headers: {
                "Authorization": `Bearer ${jToken}`,
            },
        };

        await axiosApi.get('/home/init',requestOption)
        .then((res) => {
            const result = res.data.result || {};

            // console.log(result)
            const usrCalorList = res.data.result.usrCalorList;
            const calorRecList = usrCalorList.flatMap(item => item.calorRecList ?? []);
            
            const nutritionGoal = res.data.result.nutritionGoal;


            // const usrCalorList = result.usrCalorList ?? [];

            // 소수점 첫째 자리까지 반올림
            const round1 = (value) =>
                Math.round(Number(value ?? 0) * 10) / 10;

            // 끼니별 합산 → 반올림 → 하루 전체 합산
            const sumMealNutrition = (key, digits = 1) => {

                const round = (value) =>
                    digits === 0
                        ? Math.round(Number(value ?? 0))
                        : round1(value);

                const total = usrCalorList.reduce((total, meal) => {

                    const mealTotal = (meal.calorRecList ?? []).reduce(
                        (acc, cur) => acc + Number(cur[key] ?? 0),
                        0
                    );

                    return total + round(mealTotal);

                }, 0);

                return round(total);
            };

            // 기존 일일 영양정보를 기준으로 계산값 덮어쓰기
            const dietDayInfo = {
                ...(result.dietDayInfo ?? {}),
                energy: sumMealNutrition('energy', 0),
                sodium: sumMealNutrition('sodium', 0),
                protein: sumMealNutrition('protein', 1),
                carbohydrate: sumMealNutrition('carbohydrate', 1),
                fat: sumMealNutrition('fat', 1),
            };

            setHomeData({
                dietDayInfo, // 수정
                currentBmi: result.currentBmi || {},
                weeklySodium: result.weeklySodium || [],
                weeklyExercise: result.weeklyExercise || [],
                currentBrssr: result.currentBrssr || {},
                nutritionGoal: result.nutritionGoal || {},
                exerToday: result.exerToday || {},
            });

            setGoalData(result.patntHlthGoal);

            const checkupList = result.checkupList;

            // 가장 최근 검진 데이터
            setLatestCheckup(checkupList.length > 0 ? checkupList[checkupList.length - 1] : {});

            //식단 ai 코멘트
            const toDayDietAiComment = res.data.result.toDayDietAiComment || {};

            if (toDayDietAiComment?.aiStatCd === 'S') {

                // 이미 완료된 결과
                setAiComment(toDayDietAiComment.aiAsstMsg ?? '');
                setAiCommentLoading(false);

            } else if (toDayDietAiComment?.aiStatCd === 'W') {

                // 이전 요청이 아직 처리 중
                setAiCommentLoading(true);
                aiRequestRef.current = true;

                startAiPolling(toDayDietAiComment.aiAsstMsgId);

            } else {

                // 현재 식단 버전에 해당하는 AI 요청 자체가 없음
                const today = dayjs().format('YYYYMMDD');
                // if (calorRecList.length > 0) {
                getAiComment(usrCalorList, today, nutritionGoal.sodiumGoal);
                // }
            }


            const toDayExercList = result.exerToday.list || [];
            //운동 AI 코멘트
            const toDayExercAiComment = result.exerToday.toDayExercAiComment || {}; 

            if (toDayExercAiComment?.aiStatCd === 'S') {

                setExerciseAiComment(toDayExercAiComment.aiAsstMsg ?? '');
                setExerciseAiCommentLoading(false);

            } else if (toDayExercAiComment?.aiStatCd === 'W') {

                setExerciseAiCommentLoading(true);
                exerciseAiRequestRef.current = true;

                startExerciseAiPolling(toDayExercAiComment.aiAsstMsgId);

            } else {

                const today = dayjs().format('YYYYMMDD');

                //운동 이력이 없어도 보냄
                // if (toDayExercList.length > 0) {
                    getExerciseAiComment(toDayExercList, today);
                // }
            }


        })
        .catch(error => {
            console.error(error);
        }).finally(() => {
        })

    }, []);


    ////식단 ai 코멘트 관련

    const startAiPolling = (aiAsstMsgId) => {

        if (!aiAsstMsgId) {
            return;
        }

        // 이미 같은 ID 폴링 중
        if (aiPollingIdRef.current === aiAsstMsgId) {
            return;
        }

        if (aiPollingTimerRef.current) {
            clearTimeout(aiPollingTimerRef.current);
        }

        aiPollingIdRef.current = aiAsstMsgId;

        pollAiComment(aiAsstMsgId);
    };

    const pollAiComment = async (aiAsstMsgId, attempt = 0) => {

        if (aiPollingIdRef.current !== aiAsstMsgId) {
            return;
        }

        const MAX_ATTEMPTS = 15;
        const POLLING_INTERVAL = 2000;

        if (attempt >= MAX_ATTEMPTS) {
            console.warn('AI Comment polling timeout');

            aiRequestRef.current = false;
            aiPollingIdRef.current = null;
            aiPollingTimerRef.current = null;
            setAiCommentLoading(false);

            return;
        }

        const jToken = localStorage.getItem("jToken");

        try {

            const res = await axiosApi.get(
                '/diet/aiComment/result',
                {
                    headers: {
                        Authorization: `Bearer ${jToken}`
                    },
                    params: {
                        aiAsstMsgId
                    },
                    noLoading: true,
                }
            );

            // 기다리는 동안 polling이 종료/변경됐는지 재확인
            if (aiPollingIdRef.current !== aiAsstMsgId) {
                return;
            }

            const result = res.data?.result;

            console.log('AI Comment polling:', result);

            if (!result) {
                aiRequestRef.current = false;
                aiPollingIdRef.current = null;
                aiPollingTimerRef.current = null;
                setAiCommentLoading(false);
                return;
            }

            if (result.aiStatCd === 'S') {

                setAiComment(result.aiAsstMsg ?? '');
                setAiCommentLoading(false);

                aiRequestRef.current = false;
                aiPollingIdRef.current = null;
                aiPollingTimerRef.current = null;

                return;
            }

            if (result.aiStatCd === 'F') {

                console.error('AI Comment 처리 실패');

                aiRequestRef.current = false;
                aiPollingIdRef.current = null;
                aiPollingTimerRef.current = null;
                setAiCommentLoading(false);

                return;
            }

            aiPollingTimerRef.current = setTimeout(() => {
                pollAiComment(aiAsstMsgId, attempt + 1);
            }, POLLING_INTERVAL);

        } catch (error) {

            console.error('AI Comment polling 오류:', error);

            aiRequestRef.current = false;
            aiPollingIdRef.current = null;
            aiPollingTimerRef.current = null;
            setAiCommentLoading(false);
        }
    };


    const getAiComment = async (usrCalorList, recDe, sodiumGoal) => {

        if (aiRequestRef.current) {
            return;
        }

        aiRequestRef.current = true;
        setAiCommentLoading(true);

        const aiDietData = makeAiDietData(usrCalorList, recDe, sodiumGoal);

        const loginUser = JSON.parse(sessionStorage.getItem('loginUser'));
        // 어느 병원인지 알 수 없어서 일단 012 하드코딩
        const patientId = loginUser.usrHsptlMap.find((a) => a.cnncHsptlCd === "012")?.patientId;

        const jToken = localStorage.getItem("jToken");
        const reqOptions = { 
            headers: { Authorization: `Bearer ${jToken}` } ,
            noLoading: true,
        };

        const requestData = {
            recDe,
            patientId,
            drOrglData: JSON.stringify(aiDietData)
        };

        try {

            const res = await axiosApi.post('/diet/aiComment', requestData, reqOptions);

            const aiAsstMsgId = res.data.result;
            if (aiAsstMsgId) {
                // 신규 AI 요청이 생성됨
                startAiPolling(aiAsstMsgId);
            } else {
                // 신규 요청 없음
                aiRequestRef.current = false;
                setAiCommentLoading(false);
            }

        } catch (error) {

            console.error('AI Comment 오류:', error);

            // 요청 자체가 실패했으면 재시도 가능
            aiRequestRef.current = false;
            setAiCommentLoading(false);
        }
    }

    const makeAiDietData = (usrCalorList, recDe, sodiumGoal) => {
        const meals = (usrCalorList ?? [])
        .filter(meal => (meal.calorRecList ?? []).length > 0)
        .map((meal) => {

            const calorRecList = meal.calorRecList ?? [];

            const totals = calorRecList.reduce(
                (acc, cur) => {
                    acc.energy += Number(cur.energy) || 0;
                    acc.protein += Number(cur.protein) || 0;
                    acc.sodium += Number(cur.sodium) || 0;

                    return acc;
                },
                {
                    energy: 0,
                    protein: 0,
                    sodium: 0
                }
            );

            return {
                energy: Math.round(totals.energy * 100) / 100,
                meal_type: meal.ntktmTyCd,
                protein: Math.round(totals.protein * 100) / 100,
                record_count: calorRecList.length,
                sodium: Math.round(totals.sodium * 100) / 100
            };
        });

        return {
            criteria: {
                sodium_limit_mg: sodiumGoal
            },
            evaluated_at: dayjs().format('YYYY-MM-DDTHH:mm:ssZ'),
            meals,
            nutrition_calculation_complete: true,
            record_date: dayjs(recDe, 'YYYYMMDD').format('YYYY-MM-DD')
        };
    };


    ////식단 ai 코멘트 관련

    ////운동 ai 코멘트 관련

    const getExerciseAiComment = async (toDayExercList, recDe) => {

        if (exerciseAiRequestRef.current) {
            return;
        }

        exerciseAiRequestRef.current = true;
        setExerciseAiCommentLoading(true);


        const loginUser = JSON.parse(sessionStorage.getItem('loginUser'));

        const patientId = loginUser.usrHsptlMap
            .find((a) => a.cnncHsptlCd === "012")
            ?.patientId;

        const jToken = localStorage.getItem("jToken");

        const reqOptions = {
            headers: {
                Authorization: `Bearer ${jToken}`
            },
            noLoading: true,
        };

        const aiExerciseList = (toDayExercList ?? []).map((item) => {
            const { lastMdfyDt, ...exercise } = item;
            return exercise;
        });

        const requestData = {
            recDe,
            patientId,
            drOrglData: JSON.stringify(aiExerciseList)
        };

        try {

            const res = await axiosApi.post(
                '/exercise/aiComment',
                requestData,
                reqOptions
            );

            const aiAsstMsgId = res.data.result;

            if (aiAsstMsgId) {

                startExerciseAiPolling(aiAsstMsgId);

            } else {

                exerciseAiRequestRef.current = false;
                setExerciseAiCommentLoading(false);
            }

        } catch (error) {

            console.error('Exercise AI Comment 오류:', error);

            exerciseAiRequestRef.current = false;
            setExerciseAiCommentLoading(false);
        }
    };

    const makeAiExerciseData = (toDayExercList, recDe) => {

        const exerciseList = toDayExercList ?? [];

        const totalMinutes = exerciseList.reduce(
            (sum, item) => sum + (Number(item.exrcMin) || 0),
            0
        );

        // 가장 최근 등록 시간
        const latestUpdatedAt = exerciseList
            .map(item => item.lastMdfyDt || item.frstRgstDt)
            .filter(Boolean)
            .sort()
            .at(-1);

        return {
            record_date: dayjs(recDe, 'YYYYMMDD').format('YYYY-MM-DD'),

            evaluated_at: dayjs().format('YYYY-MM-DDTHH:mm:ssZ'),

            exercise_calculation_complete: true,

            exercise: {
                record_count: exerciseList.length,
                total_minutes: totalMinutes
            },

            data_updated_at: latestUpdatedAt
                ? dayjs(latestUpdatedAt, 'YYYY-MM-DD HH:mm:ss')
                    .format('YYYY-MM-DDTHH:mm:ssZ')
                : dayjs().format('YYYY-MM-DDTHH:mm:ssZ')
        };
    };

    const startExerciseAiPolling = (aiAsstMsgId) => {

        if (!aiAsstMsgId) {
            return;
        }

        // 이미 같은 ID 폴링 중
        if (exerciseAiPollingIdRef.current === aiAsstMsgId) {
            return;
        }

        if (exerciseAiPollingTimerRef.current) {
            clearTimeout(exerciseAiPollingTimerRef.current);
        }

        exerciseAiPollingIdRef.current = aiAsstMsgId;

        pollExerciseAiComment(aiAsstMsgId);
    };


    const pollExerciseAiComment = async (aiAsstMsgId, attempt = 0) => {

        if (exerciseAiPollingIdRef.current !== aiAsstMsgId) {
            return;
        }

        const MAX_ATTEMPTS = 15;
        const POLLING_INTERVAL = 2000;

        if (attempt >= MAX_ATTEMPTS) {

            console.warn('Exercise AI Comment polling timeout');

            exerciseAiRequestRef.current = false;
            exerciseAiPollingIdRef.current = null;
            exerciseAiPollingTimerRef.current = null;
            setExerciseAiCommentLoading(false);

            return;
        }

        const jToken = localStorage.getItem("jToken");

        try {

            const res = await axiosApi.get(
                '/exercise/aiComment/result',
                {
                    headers: {
                        Authorization: `Bearer ${jToken}`
                    },
                    params: {
                        aiAsstMsgId
                    },
                    noLoading: true,
                }
            );

            // 기다리는 동안 polling이 종료/변경됐는지 재확인
            if (exerciseAiPollingIdRef.current !== aiAsstMsgId) {
                return;
            }

            const result = res.data?.result;

            console.log('Exercise AI Comment polling:', result);

            if (!result) {

                exerciseAiRequestRef.current = false;
                exerciseAiPollingIdRef.current = null;
                exerciseAiPollingTimerRef.current = null;
                setExerciseAiCommentLoading(false);

                return;
            }

            if (result.aiStatCd === 'S') {

                setExerciseAiComment(result.aiAsstMsg ?? '');
                setExerciseAiCommentLoading(false);

                exerciseAiRequestRef.current = false;
                exerciseAiPollingIdRef.current = null;
                exerciseAiPollingTimerRef.current = null;

                return;
            }

            if (result.aiStatCd === 'F') {

                console.error('Exercise AI Comment 처리 실패');

                exerciseAiRequestRef.current = false;
                exerciseAiPollingIdRef.current = null;
                exerciseAiPollingTimerRef.current = null;
                setExerciseAiCommentLoading(false);

                return;
            }

            exerciseAiPollingTimerRef.current = setTimeout(() => {
                pollExerciseAiComment(aiAsstMsgId, attempt + 1);
            }, POLLING_INTERVAL);

        } catch (error) {

            console.error('Exercise AI Comment polling 오류:', error);

            exerciseAiRequestRef.current = false;
            exerciseAiPollingIdRef.current = null;
            exerciseAiPollingTimerRef.current = null;
            setExerciseAiCommentLoading(false);
        }
    };
    ////운동 ai 코멘트 관련



    const getRandomHealthContent = async () => { 

        const jToken = localStorage.getItem("jToken");

        const loginUser = JSON.parse(sessionStorage.getItem('loginUser'));

        const usrHsptlMap = loginUser.usrHsptlMap;

        const dissAt = loginUser.usrHsptlMap.find((a) => a.cnncHsptlCd === "012").dissAt;
        const hhprsDgnssAt = loginUser.usrHsptlMap.find((a) => a.cnncHsptlCd === "012").hhprsDgnssAt;
        const dabtsDgnssAt = loginUser.usrHsptlMap.find((a) => a.cnncHsptlCd === "012").dabtsDgnssAt;


        const stauts = getPatntHealthStatus(dissAt, dabtsDgnssAt, hhprsDgnssAt);


        const requestOption = {
            headers: {
                "Authorization": `Bearer ${jToken}`,
            },
            params : {
                status : stauts
            }
        };

        await axiosApi.get('/healthContent/randContList',requestOption)
        .then((res) => {
            const result = res.data.result || {};

            setContents(result);

        })
        .catch(error => {
            console.error(error);
        }).finally(() => {
        })

    }


    const weeklyGoals = useMemo(() => {

        /* ──────────────────────
        식단 관리
        ────────────────────── */
        if (mainTab === 'diet') {
            const sodiumGoal = homeData.nutritionGoal?.sodiumGoal ?? 2000;

            const days = Array(7).fill('none');

            homeData.weeklySodium.forEach(item => {
                const day = Number(item.dayOfWeek);

                if (item.sodium == null) {
                    days[day] = 'none';
                    return;
                }

                days[day] = Number(item.sodium) <= sodiumGoal
                    ? 'success'
                    : 'fail';
            });

            return [
                {
                    title: '나트륨 섭취',
                    condition: `${sodiumGoal.toLocaleString()}mg 이하 / 주 5회 이상`,
                    days,
                    color: 'blue',
                },
            ];
        }


        /* ──────────────────────
        운동 관리
        ────────────────────── */
        const days = Array(7).fill('none');

        const today = new Date();
        const todayStr =
            today.getFullYear().toString() +
            String(today.getMonth() + 1).padStart(2, '0') +
            String(today.getDate()).padStart(2, '0');

        homeData.weeklyExercise.forEach(item => {
            const day = Number(item.dayOfWeek);

            // 미래 날짜
            if (item.recDe > todayStr) {
                days[day] = 'none';
                return;
            }

            // 오늘/과거인데 운동 기록 없음
            if (item.exrcMin == null) {
                // days[day] = 'fail';
                days[day] = 'none';
                return;
            }

            days[day] = Number(item.exrcMin) >= WEEKLY_EXERCISE_MIN_GOAL
                ? 'success'
                : 'fail';
        });

        return [
            {
                title: '운동',
                condition: `${WEEKLY_EXERCISE_MIN_GOAL}분 이상 / 주 5회 이상`,
                days,
                color: 'blue',
            },
        ];

    }, [mainTab, homeData.weeklySodium, homeData.weeklyExercise, homeData.nutritionGoal,]);

    return (
        <>
          <Header menu="homMain" />
            <div id="sub-content" data-layout="white">
                <div className='inner-wrap' data-top-padding="0">

                    {/* ── 메인 탭 (식단관리 / 운동관리) ── */}
                    <div className={style.tabBar} ref={tabBarRef}>
                        {MAIN_TABS.map(tab => (
                            <button
                                key={tab.key}
                                data-tab={tab.key}
                                className={`${style.tabItem} ${mainTab === tab.key ? style.active : ''}`}
                                onClick={() => { setMainTab(tab.key)}}
                            >
                                {tab.label}
                            </button>
                        ))}
                        <span className={style.tabIndicator} style={indicatorStyle} />
                    </div>

                    {/* ── 히어로 카드 ── */}
                    <HeroCard
                        {...hero}
                        onAction={() => hero.onAction && navigate(hero.onAction)}
                    />

                    {/* ── AI 추천 바 ── */}
                    <AiRecommendBox
                        title={mainTab === 'diet' ? 'AI 식단 추천' : 'AI 운동 추천'}
                        description={mainTab === 'diet'
                            ? (
                                aiCommentLoading
                                ? 'AI가 오늘의 식단을 분석하고 있습니다.'
                                : aiComment
                            )
                            : (
                                exerciseAiCommentLoading
                                    ? 'AI가 오늘의 운동을 분석하고 있습니다.'
                                    : exerciseAiComment
                            )
                        }
                        onClick={handleAiChat}
                    />

                    {/* ── 서브 탭 ── */}
                    <div className={style.reportCard}>
                        <div className={style.subTabBar} ref={subTabBarRef}>
                            {SUB_TABS.map(tab => (
                                <button
                                    key={tab.key}
                                    data-subtab={tab.key}
                                    className={`${style.subTabBtn} ${subTab === tab.key ? style.active : ''}`}
                                    onClick={() => setSubTab(tab.key)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                            <span className={style.subTabSlider} style={subIndicatorStyle} />
                        </div>

                        {/* ── 리포트 (일일 / 건강목표) ── */}
                        <HealthReport
                            subTab={subTab}
                            dailyList={dailyList}
                            goalData={goalData}
                            weeklyGoals={weeklyGoals}
                            latestCheckup={latestCheckup}
                            onSync={reloadSteps}
                        />
                    </div>

                    {/* ── 추천 콘텐츠 ── */}
                    <RecommendContent
                        items={contents}
                        onMoreClick={() => {navigate(URL.HEALTH_INFORM)}}
                    />

                </div>
            </div>
            <Navbar />
        </>
    );
};

export default HomeMain;